import cn from 'classnames';
import Badge from 'components/badge';
import Price from 'components/price';
import RatingBadge from 'components/rating-badge';
import { useConfig } from 'config/use-config';
import { OrderType } from 'constants/order';
import { useCommon } from 'contexts/common';
import { useSegmentationContext } from 'contexts/segmentation';
import useIsClient from 'hooks/common/use-is-client';
import useMobile from 'hooks/common/use-mobile';
import useProduct from 'hooks/product/use-product';
import { CollectionProduct } from 'models/collection/collection';
import Image from 'next/legacy/image';
import React, { memo, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import hasPersonalizedDiscount from 'utils/has-personalized-discount';
import { generalImageLoader } from 'utils/image-loader';
import { minusPercent } from 'utils/product';
import { CARD_TYPES } from '.';
import AccentuateBadges from './accentuate-badges';

interface Props {
  isBestSeller?: boolean;
  product: CollectionProduct;
  shouldShowBlackRibbon?: boolean;
  showSubscriptionPrice: boolean;
  starSize?: 'small' | 'medium';
  type: (typeof CARD_TYPES)[keyof typeof CARD_TYPES];
  orderType?: OrderType;
  additionalPriceInfo: React.ReactNode;
  renderInCartBadge?: (wrap?: (badge: React.ReactNode) => React.ReactNode) => React.ReactNode;
}

/**
 * Product Card Content. Displays product image, title, rating, price and subscription price.
 * @TODO : After migration to PDEX stacks, logics of discount calculations will be obsolete
 */
const Content: React.FC<Props> = ({
  product,
  shouldShowBlackRibbon,
  showSubscriptionPrice,
  // starSize,
  type,
  isBestSeller,
  additionalPriceInfo,
  renderInCartBadge,
}) => {
  const { isClient } = useIsClient();
  const isMobile = useMobile();
  const { globalDiscount } = useCommon();
  const { subscriptionDiscount } = useConfig();
  const hasDiscount = hasPersonalizedDiscount(product, globalDiscount, true);
  const { availableForSale } = product;

  const { comparePrice, price } = useProduct(product, {
    isSubscriptionSelected: showSubscriptionPrice,
  });

  const { shouldOmitPersonalization, multipleSegmentation } = useSegmentationContext();
  const lastSegmentation = multipleSegmentation?.lastSegmentation;

  const subTitle = useMemo(() => {
    if (shouldOmitPersonalization) return product.getProductSubtitle();
    return product.getProductSubtitle({
      segmentation: lastSegmentation,
    });
  }, [product, shouldOmitPersonalization, lastSegmentation]);

  const shouldShowBadgesSection =
    type !== CARD_TYPES.SLIDER &&
    isClient &&
    (isBestSeller || product.getRibbon().ribbonText || shouldShowBlackRibbon);

  const displayTitle = useMemo(() => {
    return product.getProductTitle();
  }, [product]);

  const ribbon = useMemo(() => product.getRibbon(), [product]);

  // QA Temporary: Used during PDEX transition
  const titleSource = product.getProductTitle() ? 'accentuate' : 'product';

  const subscriptionValue = hasDiscount
    ? globalDiscount.discount?.firstSubscriptionOrderDiscount
    : subscriptionDiscount;

  const firstCheapestAvailableSubsVariant = useMemo(() => {
    // if smart collection item is dealt here, we show price
    if (product.getIsSmartCollectionItem()) {
      return +product.variants[0].priceV2.amount;
    }

    const _firstCheapestAvailableSubsVariant = product.getFirstCheapestAvailableSubsVariant();

    return subscriptionValue && _firstCheapestAvailableSubsVariant
      ? minusPercent(+_firstCheapestAvailableSubsVariant.priceV2.amount, subscriptionValue)
      : null;
  }, [product, subscriptionValue]);

  const badgeSection = useMemo(() => {
    return (
      <>
        {shouldShowBlackRibbon ? (
          <Badge
            type="General"
            data-test="black-deals-badge"
            className="bg-other-black! text-text-dark-bg-contrast-white!"
          >
            BLACK DEAL
          </Badge>
        ) : (
          <>
            {isBestSeller && (
              <Badge type="Bestseller" data-test="personalized-ribbon-text">
                <FormattedMessage id="common:bestseller" />
              </Badge>
            )}
            <AccentuateBadges
              title={ribbon.ribbonText}
              subTitle={ribbon.ribbonText2}
              color={ribbon.ribbonColor}
              product={product}
              data-test="accentuate-badges"
            />
          </>
        )}
      </>
    );
  }, [
    isBestSeller,
    product,
    ribbon.ribbonColor,
    ribbon.ribbonText,
    ribbon.ribbonText2,
    shouldShowBlackRibbon,
  ]);

  const wrapBadgesSection = useCallback(
    (inCartBadge: React.ReactNode) =>
      (shouldShowBadgesSection || inCartBadge) && (
        <div
          className="absolute top-1.5 left-1.5 z-10 flex w-full gap-1.5 lg:top-2 lg:left-2 lg:has-[[data-test=in-cart-badge]]:inset-0 lg:has-[[data-test=in-cart-badge]]:items-start"
          data-test="badges-section"
        >
          {badgeSection}
          {inCartBadge}
        </div>
      ),
    [badgeSection, shouldShowBadgesSection]
  );

  const renderWeight = useCallback(
    (weight: string, index: number, arr: string[]) => (
      <React.Fragment key={index}>
        {weight}
        {index < arr.length - 1 ? ' / ' : ''}
      </React.Fragment>
    ),
    []
  );

  return (
    <div
      className={cn(
        'group bg-other-white relative z-0 h-full min-h-[172px] w-full rounded-lg pr-3 pl-2 shadow-xs lg:px-2 lg:pt-2 lg:pb-4',
        type === CARD_TYPES.SLIDER && 'px-2 py-3 lg:px-4 lg:pt-2 lg:pb-4'
      )}
      data-test="product-card"
    >
      <div
        className={cn(
          'flex h-full gap-2 lg:flex-col lg:gap-2.5',
          shouldShowBadgesSection ? 'pt-7 lg:pt-0' : 'pt-2 lg:pt-0',
          'max-lg:has-[[data-test=badges-section]_[data-test=in-cart-badge]]:pt-7',
          type === CARD_TYPES.SLIDER || type === CARD_TYPES.SLIDER_LARGE ? 'flex-col pb-0' : 'pb-3'
        )}
        data-test="product-card-content"
      >
        <div
          className={cn(
            'contents',
            type === CARD_TYPES.REGULAR
              ? 'lg:has-[[data-test=in-cart-badge]]:relative lg:has-[[data-test=in-cart-badge]]:block'
              : 'has-[[data-test=in-cart-badge]]:relative has-[[data-test=in-cart-badge]]:block'
          )}
          data-test="product-card-header"
        >
          {type === CARD_TYPES.REGULAR &&
            (renderInCartBadge ? renderInCartBadge(wrapBadgesSection) : wrapBadgesSection(null))}
          {/* Product Image */}
          <div
            className={cn(
              'flex items-center justify-center',
              !availableForSale && 'opacity-70',
              type === CARD_TYPES.REGULAR && 'min-h-[130px] min-w-[130px] lg:min-h-[223px]'
            )}
            data-test="product-image-container"
          >
            <div
              className={cn(
                'relative flex w-full items-center justify-center',
                type === CARD_TYPES.SLIDER && 'h-full'
              )}
              data-test="product-image-wrapper"
            >
              {!availableForSale && (
                <Badge
                  className="bg-grey-50 text-text-primary absolute top-[50%] left-[50%] z-20 flex h-10! w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-sm text-sm"
                  data-test="out-of-stock-badge"
                >
                  <FormattedMessage id="pdp:buy-panel:product-out-stock" />
                </Badge>
              )}
              {product.image.src && (
                <div className={cn('')} data-test="product-image">
                  <div
                    className={cn(
                      type === CARD_TYPES.REGULAR &&
                        'transition-all duration-300 ease-in-out lg:group-hover:h-[200px] lg:group-hover:w-[200px]',
                      type === CARD_TYPES.SLIDER_LARGE
                        ? 'h-[270px] w-[270px] lg:h-[272px] lg:w-[272px]'
                        : 'h-[130px] w-[130px] lg:h-[188px] lg:w-[188px]'
                    )}
                    data-test="image-container"
                  >
                    <Image
                      data-test="product-image-element"
                      loader={generalImageLoader}
                      src={product.image.src}
                      alt={product.image.alt || product.title || ''}
                      width={800}
                      height={800}
                      sizes="(min-width: 1024px) 33vw, (min-width: 769px) 50vw, 50vw"
                      className="h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          {type !== CARD_TYPES.REGULAR && renderInCartBadge?.()}
        </div>
        {/* Product Data */}
        <div
          className={cn(
            'bg-other-white flex flex-col items-start gap-1 text-left align-top',
            type === CARD_TYPES.SLIDER && 'p-0'
          )}
          data-test="product-data"
        >
          <RatingBadge sku={product.id} data-test="product-rating" />
          <section
            className="font-mindset text-text-light-bg-primary line-clamp-2 self-stretch overflow-x-visible overflow-y-clip pt-2 text-lg leading-6 tracking-wide lg:pt-0"
            data-test="product-title"
            data-test-pdp-title={product.getProductTitle()}
            data-test-title-source={titleSource}
          >
            {displayTitle}
          </section>
          {/* Product Subtitle */}
          {isClient && subTitle && (
            <div
              data-test="product-subtitle"
              className={cn(
                'text-text-secondary text-sm leading-[18px]',
                isMobile ? 'line-clamp-4' : 'line-clamp-2'
              )}
            >
              {subTitle}
            </div>
          )}
          <div
            className={cn(
              'font-black',
              comparePrice ? 'text-text-light-bg-discounted-price' : 'text-text-light-bg-primary'
            )}
            data-test="product-price"
          >
            {product.getShouldShowFromPrice() ? (
              <span data-test="price-from">
                <FormattedMessage id="cdp:general:price_from" />
                &nbsp;
              </span>
            ) : null}
            <span>
              <Price value={firstCheapestAvailableSubsVariant ?? price} />
            </span>
            {comparePrice && (
              <span
                className="text-text-secondary ml-2 text-sm font-normal line-through"
                data-test="compare-price"
              >
                <Price value={comparePrice} />
              </span>
            )}{' '}
          </div>
          {additionalPriceInfo && (
            <div className="text-text-secondary text-sm font-normal">{additionalPriceInfo}</div>
          )}
        </div>
        <p
          className={cn(
            'text-text-light-bg-secondary mt-auto text-left text-base leading-5 whitespace-pre-wrap',
            type === CARD_TYPES.REGULAR && 'hidden lg:line-clamp-2'
          )}
          data-test="product-weights"
        >
          {product.getWeights().map(renderWeight)}
        </p>
      </div>
    </div>
  );
};

export default memo(Content);
